<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import InboxesAPI from 'dashboard/api/inboxes';
import OnboardingAPI from 'dashboard/api/onboarding';
import Input from 'dashboard/components-next/input/Input.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Modal from 'dashboard/components/Modal.vue';
import InboxFinalDeleteModal from 'dashboard/routes/dashboard/settings/inbox/components/InboxFinalDeleteModal.vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const props = defineProps({
  progress: { type: Object, required: true },
  draftData: { type: Object, default: null },
});
const emit = defineEmits([
  'advance',
  'refresh',
  'update:can-advance',
  'update:draft-data',
]);

const { t } = useI18n();
const store = useStore();

// Same polling contract as GupshupOnboarding.vue (dashboard/routes/dashboard/settings/inbox) —
// reused here instead of reimplemented, per ADR-029 (step 1 applies immediately).
const POLLING_INTERVAL_MS = 8000;
const MAX_POLLING_ATTEMPTS = 60;
// How often we check whether the user (or a blocked popup) closed the connection
// window before polling ever confirmed success.
const CLOSE_CHECK_INTERVAL_MS = 1000;

// Steps whose STEP_DETECTORS check settings that live on a specific inbox (assignment
// policy, working hours, lead return rule, templates, CSAT survey) — see
// app/models/onboarding_progress.rb. Whenever the inbox this onboarding run points at
// changes, these can no longer be trusted as "done for this inbox" and are reopened.
const INBOX_DEPENDENT_STEPS = [4, 5, 6, 8, 9];

const inboxName = ref('');
// phone_migration isn't persisted anywhere on the Inbox/Channel — it's only a one-off
// param for the Gupshup embed link — so we mirror it into draft_data.step_1 ourselves
// just to keep the checkbox's state across remounts/reloads.
const phoneMigration = ref(props.draftData?.phone_migration ?? false);
const isConnecting = ref(false);
const isConnected = ref(false);
const existingInbox = ref(null);
const pendingInbox = ref(null);
// Already-connected Gupshup inboxes the account has (e.g. set up before this onboarding
// session, or in a previous one) — offered as an alternative to creating a new one.
const connectedInboxes = ref([]);
const mode = ref('create'); // 'select' | 'create'
const selectedInboxId = ref(null);
const isSelectingExisting = ref(false);
const isUpdatingSelection = ref(false);
// Two-step delete confirmation, matching the Settings > Inboxes flow (type-to-confirm,
// then a final "this can't be undone" warning) — see InboxFinalDeleteModal.
const showDeletePopup = ref(false);
const showFinalDeletePopup = ref(false);
const inboxPendingDeletion = ref(null);
const popupWindow = ref(null);
const pollingIntervalId = ref(null);
const closeWatcherId = ref(null);

// Single modal that owns the connection attempt (connecting/error) — the Gupshup embed
// link still opens as a native popup window (external OAuth-style flow), but everything
// else renders inside this modal. Success has no screen of its own: there is nothing left
// to do on this step, so we close the modal and advance straight to the next one.
const connectionModalOpen = ref(false);
const connectionState = ref('connecting'); // 'connecting' | 'error'
const connectionErrorMessage = ref('');

const isStepAlreadyCompleted = computed(() =>
  props.progress.completed_steps.includes(1)
);

const deleteConfirmText = computed(
  () =>
    `${t('INBOX_MGMT.DELETE.CONFIRM.YES')} ${inboxPendingDeletion.value?.name}`
);
const deleteRejectText = computed(
  () =>
    `${t('INBOX_MGMT.DELETE.CONFIRM.NO')} ${inboxPendingDeletion.value?.name}`
);
const confirmDeleteMessage = computed(() =>
  t('INBOX_MGMT.DELETE.CONFIRM.MESSAGE', {
    inboxName: inboxPendingDeletion.value?.name,
  })
);
const confirmPlaceHolderText = computed(() =>
  t('INBOX_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
    inboxName: inboxPendingDeletion.value?.name,
  })
);
const confirmWarningMessage = computed(() =>
  t('INBOX_MGMT.DELETE.CONFIRM.WARNING')
);

watch(phoneMigration, value => {
  emit('update:draft-data', { phone_migration: value });
});

const stopConnectionPolling = () => {
  if (pollingIntervalId.value) {
    clearInterval(pollingIntervalId.value);
    pollingIntervalId.value = null;
  }
};

const stopCloseWatcher = () => {
  if (closeWatcherId.value) {
    clearInterval(closeWatcherId.value);
    closeWatcherId.value = null;
  }
};

// Persists completion and hands control to the wizard's own Next. We deliberately don't
// emit 'refresh' here: the advance that follows re-fetches progress anyway, and racing
// both requests could land the stale (still step 1) response last.
//
// inboxId is recorded into draft_data.step_1 so later steps (e.g. the template submission
// job) know exactly which inbox this onboarding run picked, instead of having to guess
// from the account's inboxes. Steps that configure settings on that inbox specifically are
// reopened, since they were (if completed at all) validated against a different inbox.
const completeStepAndAdvance = async inboxId => {
  try {
    const completedSteps = new Set([...props.progress.completed_steps, 1]);
    INBOX_DEPENDENT_STEPS.forEach(step => completedSteps.delete(step));

    await OnboardingAPI.update({
      completed_steps: [...completedSteps],
      draft_data: {
        step_1: { ...(props.draftData || {}), inbox_id: inboxId },
      },
    });
    emit('update:can-advance', true);
    useAlert(t('ONBOARDING.STEP_1.CONNECTED_ALERT'));
    emitter.emit(BUS_EVENTS.INBOX_CHANGED);
    emit('advance');
  } catch (error) {
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  }
};

// Only the polling confirmation (connected: true) is allowed to complete the step —
// this watcher exists purely to catch every OTHER way the popup can go away (the user
// closes it manually, it gets blocked, or it's closed for any other reason) so we never
// silently leave the form stuck on "Conectando..." or mark the step done without proof.
//
// Gupshup's embed flow often closes the popup itself right after a successful connection —
// frequently faster than pollConnectionStatus's next 8s tick would catch it. So before
// declaring failure, check status once more immediately: only report "closed before
// finishing" if that check also comes back not-connected.
const watchPopupClosed = inboxId => {
  stopCloseWatcher();
  closeWatcherId.value = setInterval(async () => {
    if (popupWindow.value && !popupWindow.value.closed) return;

    stopCloseWatcher();
    if (!isConnecting.value || isConnected.value) return;

    try {
      const response = await InboxesAPI.getGupshupConnectionStatus(inboxId);
      if (response.data?.connected) {
        stopConnectionPolling();
        isConnecting.value = false;
        isConnected.value = true;
        connectionModalOpen.value = false;
        await completeStepAndAdvance(inboxId);
        return;
      }
    } catch (error) {
      // Fall through to the error below — the popup is gone either way.
    }

    stopConnectionPolling();
    isConnecting.value = false;
    connectionErrorMessage.value = t('ONBOARDING.STEP_1.POPUP_CLOSED_ERROR');
    connectionState.value = 'error';
  }, CLOSE_CHECK_INTERVAL_MS);
};

const closeConnectionPopup = () => {
  try {
    popupWindow.value?.close();
  } catch (error) {
    // Ignore close errors — the popup may already be closed by the user.
  }
};

const pollConnectionStatus = inboxId => {
  stopConnectionPolling();
  let attempts = 0;

  pollingIntervalId.value = setInterval(async () => {
    attempts += 1;
    if (attempts > MAX_POLLING_ATTEMPTS) {
      stopConnectionPolling();
      stopCloseWatcher();
      isConnecting.value = false;
      connectionErrorMessage.value = t('ONBOARDING.STEP_1.ERROR');
      connectionState.value = 'error';
      return;
    }

    try {
      const response = await InboxesAPI.getGupshupConnectionStatus(inboxId);
      if (response.data?.connected) {
        stopConnectionPolling();
        stopCloseWatcher();
        closeConnectionPopup();
        isConnecting.value = false;
        isConnected.value = true;
        connectionModalOpen.value = false;
        await completeStepAndAdvance(inboxId);
      }
    } catch (error) {
      // Keep polling until max attempts is reached.
    }
  }, POLLING_INTERVAL_MS);
};

// Returns false when the browser blocked the popup (window.open returns null/undefined,
// or a window that's already closed) so the caller can fail loudly instead of silently
// polling forever for a connection window that was never actually shown.
const openConnectionPopup = embedLink => {
  const width = 1024;
  const height = 800;
  const left = Math.max(0, (window.screen.width - width) / 2);
  const top = Math.max(0, (window.screen.height - height) / 2);

  popupWindow.value = window.open(
    embedLink,
    'gupshup_onboarding',
    `width=${width},height=${height},left=${left},top=${top}`
  );

  return !!popupWindow.value && !popupWindow.value.closed;
};

const connect = async () => {
  if (!inboxName.value || isConnecting.value) return;

  isConnecting.value = true;
  connectionErrorMessage.value = '';
  connectionState.value = 'connecting';
  connectionModalOpen.value = true;
  try {
    if (!pendingInbox.value) {
      const { data: createdInbox } = await InboxesAPI.create({
        name: inboxName.value,
        channel: { type: 'gupshup' },
      });
      pendingInbox.value = createdInbox;
    }
    const inbox = pendingInbox.value;

    const { data } = await InboxesAPI.getGupshupOnboardingEmbedLink(inbox.id, {
      phone_migration: phoneMigration.value,
    });
    const embedLink = data?.embed_link || data?.embedLink;
    if (!embedLink) {
      throw new Error('missing embed link');
    }

    if (!openConnectionPopup(embedLink)) {
      isConnecting.value = false;
      connectionErrorMessage.value = t('ONBOARDING.STEP_1.POPUP_BLOCKED_ERROR');
      connectionState.value = 'error';
      return;
    }

    pollConnectionStatus(inbox.id);
    watchPopupClosed(inbox.id);
  } catch (error) {
    isConnecting.value = false;
    connectionErrorMessage.value = error?.response?.data
      ?.gupshup_feature_disabled
      ? t('ONBOARDING.STEP_1.FEATURE_DISABLED_ERROR')
      : t('ONBOARDING.STEP_1.ERROR');
    connectionState.value = 'error';
  }
};

// Cancel button (while connecting) and the modal's own close affordances
// (X button / Escape / backdrop) while a connection attempt is in flight.
const cancelConnecting = () => {
  stopConnectionPolling();
  stopCloseWatcher();
  closeConnectionPopup();
  isConnecting.value = false;
  connectionModalOpen.value = false;
};

const retryConnect = () => {
  connectionModalOpen.value = false;
  connect();
};

const handleConnectionModalClose = () => {
  if (connectionState.value === 'connecting') {
    cancelConnecting();
    return;
  }
  connectionModalOpen.value = false;
};

// Selecting an existing, already-connected inbox skips the create+connect flow
// entirely — there's nothing left to prove, so just mark the step completed.
const selectExisting = async () => {
  if (!selectedInboxId.value || isSelectingExisting.value) return;

  isSelectingExisting.value = true;
  try {
    await completeStepAndAdvance(selectedInboxId.value);
  } finally {
    isSelectingExisting.value = false;
  }
};

const switchToCreate = () => {
  mode.value = 'create';
};

const switchToSelect = () => {
  mode.value = 'select';
};

// Lets the admin create a brand new inbox instead of continuing with any existing one,
// even after step 1 is already marked complete. Nothing is persisted until they actually
// finish connecting the new inbox (completeStepAndAdvance handles that, same as always) —
// this only resets the local view back to the create form.
const startCreatingNewInbox = () => {
  isConnected.value = false;
  inboxName.value = '';
  pendingInbox.value = null;
  emit('update:can-advance', false);
};

// Reverts startCreatingNewInbox without creating anything — back to whichever inbox was
// showing as connected before.
const cancelCreatingNewInbox = () => {
  isConnected.value = true;
  const inbox =
    connectedInboxes.value.find(i => i.id === selectedInboxId.value) ||
    existingInbox.value;
  inboxName.value = inbox?.name || '';
  emit('update:can-advance', true);
};

// Lets the admin change which connected Gupshup inbox this onboarding run points at,
// even after step 1 was already marked complete (e.g. auto-detected because the account
// had inboxes before the wizard ever ran). The steps that configure settings on an inbox
// were (if completed) validated against the previous one, so they're reopened here too —
// 'refresh' tells the wizard shell to re-fetch progress so its step indicators catch up.
const updateInboxSelection = async inbox => {
  if (isUpdatingSelection.value || inbox.id === selectedInboxId.value) return;

  isUpdatingSelection.value = true;
  try {
    const completedSteps = new Set(props.progress.completed_steps);
    INBOX_DEPENDENT_STEPS.forEach(step => completedSteps.delete(step));

    await OnboardingAPI.update({
      completed_steps: [...completedSteps],
      draft_data: {
        step_1: { ...(props.draftData || {}), inbox_id: inbox.id },
      },
    });
    selectedInboxId.value = inbox.id;
    existingInbox.value = inbox;
    inboxName.value = inbox.name;
    useAlert(t('ONBOARDING.STEP_1.INBOX_CHANGED_ALERT'));
    emitter.emit(BUS_EVENTS.INBOX_CHANGED);
    emit('refresh');
  } catch (error) {
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  } finally {
    isUpdatingSelection.value = false;
  }
};

const openDelete = inbox => {
  inboxPendingDeletion.value = inbox;
  showDeletePopup.value = true;
};

const closeDelete = () => {
  showDeletePopup.value = false;
  inboxPendingDeletion.value = null;
};

const confirmDeletion = () => {
  showDeletePopup.value = false;
  showFinalDeletePopup.value = true;
};

const closeFinalDelete = () => {
  showFinalDeletePopup.value = false;
  inboxPendingDeletion.value = null;
};

// Removing an inbox drops it from every list here. If it was the one this onboarding run
// points at, step 1 (and the inbox-dependent steps above) no longer have anything valid to
// point at, so they're reopened the same way a selection change reopens them.
const finalConfirmDeletion = async () => {
  const deletedInbox = inboxPendingDeletion.value;
  showFinalDeletePopup.value = false;
  inboxPendingDeletion.value = null;
  if (!deletedInbox) return;

  try {
    await store.dispatch('inboxes/delete', deletedInbox.id);
    useAlert(t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE'));
    return;
  }

  connectedInboxes.value = connectedInboxes.value.filter(
    inbox => inbox.id !== deletedInbox.id
  );
  if (pendingInbox.value?.id === deletedInbox.id) {
    pendingInbox.value = null;
    inboxName.value = '';
  }

  if (selectedInboxId.value !== deletedInbox.id) return;

  const fallbackInbox = connectedInboxes.value[0] || null;
  selectedInboxId.value = fallbackInbox?.id ?? null;

  if (!isStepAlreadyCompleted.value) {
    // Was only a candidate in the pre-completion picker — nothing else to reopen.
    if (!fallbackInbox) mode.value = 'create';
    return;
  }

  const completedSteps = new Set(props.progress.completed_steps);
  completedSteps.delete(1);
  INBOX_DEPENDENT_STEPS.forEach(step => completedSteps.delete(step));

  try {
    await OnboardingAPI.update({
      completed_steps: [...completedSteps],
      draft_data: { step_1: { ...(props.draftData || {}), inbox_id: null } },
    });
  } catch (error) {
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  }

  isConnected.value = false;
  existingInbox.value = fallbackInbox;
  inboxName.value = '';
  mode.value = fallbackInbox ? 'select' : 'create';
  emit('update:can-advance', false);
  emitter.emit(BUS_EVENTS.INBOX_CHANGED);
  emit('refresh');
};

onMounted(async () => {
  if (!isStepAlreadyCompleted.value) {
    emit('update:can-advance', false);

    try {
      const response = await InboxesAPI.get();
      const inboxes = response.data?.payload || [];
      pendingInbox.value =
        inboxes.find(
          inbox =>
            inbox.channel_type === 'Channel::Gupshup' && !inbox.phone_number
        ) || null;
      inboxName.value = pendingInbox.value?.name || '';

      connectedInboxes.value = inboxes.filter(
        inbox =>
          inbox.channel_type === 'Channel::Gupshup' && !!inbox.phone_number
      );
      if (connectedInboxes.value.length > 0) {
        mode.value = 'select';
        selectedInboxId.value = connectedInboxes.value[0].id;
      }
    } catch (error) {
      // Non-blocking: at worst the user connects with a freshly created inbox.
    }
    return;
  }

  try {
    const response = await InboxesAPI.get();
    const inboxes = response.data?.payload || [];

    connectedInboxes.value = inboxes.filter(
      inbox => inbox.channel_type === 'Channel::Gupshup' && !!inbox.phone_number
    );
    const draftInboxId = props.draftData?.inbox_id;
    const preSelected = connectedInboxes.value.find(
      inbox => inbox.id === draftInboxId
    );
    selectedInboxId.value =
      preSelected?.id ?? connectedInboxes.value[0]?.id ?? null;
    existingInbox.value =
      preSelected ?? connectedInboxes.value[0] ?? inboxes[0] ?? null;

    inboxName.value = existingInbox.value?.name || '';
    isConnected.value = true;
    emit('update:can-advance', true);
  } catch (error) {
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  }
});

onBeforeUnmount(() => {
  stopConnectionPolling();
  stopCloseWatcher();
});
</script>

<template>
  <div
    class="flex flex-col items-center justify-start w-full h-full p-8 md:px-16"
  >
    <div class="flex flex-col items-start w-full max-w-2xl gap-4 text-left">
      <div class="flex items-center flex-wrap gap-3">
        <h1 class="text-2xl font-semibold text-n-slate-12">
          {{ t('ONBOARDING.STEP_1.TITLE') }}
        </h1>
        <a
          href="https://help.leadster.com.br/article/423-como-conectar-seu-numero-de-whatsapp-a-plataforma-da-leadster"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center gap-1 px-3 py-1 text-xs rounded-full shrink-0 bg-n-brand/10 text-n-brand hover:brightness-110"
        >
          <i class="i-lucide-circle-help text-sm" />
          {{ t('ONBOARDING.STEP_1.TUTORIAL_LINK') }}
        </a>
      </div>
      <p class="text-n-slate-12">
        {{ t('ONBOARDING.STEP_1.SUBTITLE') }}
        <span class="font-normal text-n-slate-10">{{
          t('ONBOARDING.REQUIRED_BADGE')
        }}</span>
      </p>
      <p class="text-n-slate-11">{{ t('ONBOARDING.STEP_1.DESCRIPTION') }}</p>

      <div
        v-if="mode === 'select' && !isConnected"
        class="flex flex-col w-full max-w-2xl gap-4"
      >
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('ONBOARDING.STEP_1.SELECT_EXISTING.LABEL') }}
        </p>
        <div class="flex flex-col gap-2">
          <label
            v-for="inbox in connectedInboxes"
            :key="inbox.id"
            class="flex items-center gap-2 p-3 border rounded-lg cursor-pointer border-n-weak"
          >
            <input
              v-model="selectedInboxId"
              type="radio"
              name="existing-inbox"
              :value="inbox.id"
            />
            <span class="text-n-slate-12">{{ inbox.name }}</span>
            <span class="text-sm text-n-slate-11">{{
              inbox.phone_number
            }}</span>
            <button
              type="button"
              class="ml-auto i-lucide-trash-2 size-4 shrink-0 text-n-slate-10 hover:text-n-ruby-9"
              :aria-label="t('INBOX_MGMT.DELETE.BUTTON_TEXT')"
              @click.stop.prevent="openDelete(inbox)"
            />
          </label>
        </div>
        <Button
          type="button"
          color="blue"
          icon="i-lucide-check"
          :label="t('ONBOARDING.STEP_1.SELECT_EXISTING.USE_BUTTON')"
          :is-loading="isSelectingExisting"
          :disabled="!selectedInboxId || isSelectingExisting"
          class="self-start"
          @click="selectExisting"
        />
        <button
          type="button"
          class="text-sm underline w-fit text-n-slate-11 hover:text-n-slate-12"
          @click="switchToCreate"
        >
          {{ t('ONBOARDING.STEP_1.SELECT_EXISTING.CREATE_NEW_LINK') }}
        </button>
      </div>

      <div v-else class="flex flex-col w-full max-w-2xl gap-4">
        <button
          v-if="connectedInboxes.length > 0 && !isConnected"
          type="button"
          class="text-sm underline w-fit text-n-slate-11 hover:text-n-slate-12"
          @click="
            isStepAlreadyCompleted ? cancelCreatingNewInbox() : switchToSelect()
          "
        >
          {{ t('ONBOARDING.STEP_1.SELECT_EXISTING.BACK_TO_LIST_LINK') }}
        </button>
        <Input
          v-model="inboxName"
          :label="t('ONBOARDING.STEP_1.INBOX_NAME_LABEL')"
          :placeholder="t('ONBOARDING.STEP_1.INBOX_NAME_PLACEHOLDER')"
          :disabled="isConnecting || isConnected || !!pendingInbox"
        />
        <div class="flex flex-col gap-1">
          <label class="flex items-center gap-2 cursor-pointer w-fit">
            <Checkbox
              v-model="phoneMigration"
              :disabled="isConnecting || isConnected"
            />
            <span class="text-n-slate-12">
              {{ t('ONBOARDING.STEP_1.PHONE_MIGRATION_LABEL') }}
            </span>
          </label>
          <p class="text-sm text-n-slate-11">
            {{ t('ONBOARDING.STEP_1.PHONE_MIGRATION_HELPER') }}
          </p>
          <p class="text-sm text-n-slate-11">
            {{ t('ONBOARDING.STEP_1.PHONE_MIGRATION_WARNING') }}
          </p>
        </div>

        <Button
          v-if="!isConnected"
          type="button"
          variant="outline"
          color="blue"
          icon="i-lucide-plug"
          :label="t('ONBOARDING.STEP_1.CONNECT')"
          :is-loading="isConnecting"
          :disabled="!inboxName || isConnecting"
          class="self-start"
          @click="connect"
        />
        <div v-else class="flex flex-col items-start gap-2">
          <span
            class="flex items-center gap-1 px-4 py-2 font-medium rounded-full bg-n-teal-9/10 text-n-teal-11"
          >
            <fluent-icon icon="checkmark" size="16" />
            {{ t('ONBOARDING.STEP_1.CONNECTED_LABEL') }}
          </span>
          <p class="text-sm text-n-slate-11">
            {{ t('ONBOARDING.STEP_1.CONNECTED') }}
          </p>

          <!-- Connected Gupshup inbox(es): when there's more than one, let the admin change
               which one this onboarding run points at, even though step 1 is already marked
               complete (most often because it was auto-detected on load). Either way, each
               one can be deleted straight from here. -->
          <div
            v-if="connectedInboxes.length > 0"
            class="flex flex-col w-full gap-2 mt-2"
          >
            <p
              v-if="connectedInboxes.length > 1"
              class="text-sm text-n-slate-11"
            >
              {{ t('ONBOARDING.STEP_1.SELECT_EXISTING.CHANGE_LABEL') }}
            </p>
            <div
              v-for="inbox in connectedInboxes"
              :key="inbox.id"
              class="flex items-center gap-2 p-3 border rounded-lg border-n-weak"
            >
              <input
                v-if="connectedInboxes.length > 1"
                type="radio"
                name="active-inbox"
                :value="inbox.id"
                :checked="inbox.id === selectedInboxId"
                :disabled="isUpdatingSelection"
                @change="updateInboxSelection(inbox)"
              />
              <span class="text-n-slate-12">{{ inbox.name }}</span>
              <span class="text-sm text-n-slate-11">{{
                inbox.phone_number
              }}</span>
              <button
                type="button"
                class="ml-auto i-lucide-trash-2 size-4 shrink-0 text-n-slate-10 hover:text-n-ruby-9"
                :aria-label="t('INBOX_MGMT.DELETE.BUTTON_TEXT')"
                @click.stop.prevent="openDelete(inbox)"
              />
            </div>
          </div>
          <span v-else-if="existingInbox" class="text-sm text-n-slate-11">{{
            existingInbox.name
          }}</span>

          <button
            type="button"
            class="mt-2 text-sm underline w-fit text-n-slate-11 hover:text-n-slate-12"
            @click="startCreatingNewInbox"
          >
            {{ t('ONBOARDING.STEP_1.SELECT_EXISTING.CREATE_NEW_LINK') }}
          </button>
        </div>
      </div>

      <Modal
        :show="connectionModalOpen"
        :on-close="handleConnectionModalClose"
        :show-close-button="connectionState !== 'connecting'"
        :close-on-backdrop-click="connectionState !== 'connecting'"
      >
        <div class="flex flex-col items-center gap-4 p-8 text-center">
          <template v-if="connectionState === 'connecting'">
            <i
              class="text-4xl i-lucide-loader-circle animate-spin text-n-brand"
            />
            <h3 class="text-lg font-medium text-n-slate-12">
              {{ t('ONBOARDING.STEP_1.MODAL.CONNECTING_TITLE') }}
            </h3>
            <p class="text-n-slate-11">
              {{ t('ONBOARDING.STEP_1.MODAL.CONNECTING_DESCRIPTION') }}
            </p>
            <Button
              type="button"
              variant="faded"
              color="slate"
              :label="t('ONBOARDING.STEP_1.MODAL.CANCEL')"
              @click="cancelConnecting"
            />
          </template>

          <template v-else>
            <i class="text-4xl i-lucide-circle-alert text-n-ruby-9" />
            <h3 class="text-lg font-medium text-n-slate-12">
              {{ t('ONBOARDING.STEP_1.MODAL.ERROR_TITLE') }}
            </h3>
            <p class="text-n-slate-11">{{ connectionErrorMessage }}</p>
            <div class="flex gap-2">
              <Button
                type="button"
                variant="faded"
                color="slate"
                :label="t('ONBOARDING.STEP_1.MODAL.CANCEL')"
                @click="connectionModalOpen = false"
              />
              <Button
                type="button"
                color="blue"
                :label="t('ONBOARDING.STEP_1.MODAL.RETRY')"
                @click="retryConnect"
              />
            </div>
          </template>
        </div>
      </Modal>

      <woot-confirm-delete-modal
        v-if="showDeletePopup"
        v-model:show="showDeletePopup"
        :title="t('INBOX_MGMT.DELETE.CONFIRM.TITLE')"
        :message="confirmDeleteMessage"
        :warning-message="confirmWarningMessage"
        :confirm-text="deleteConfirmText"
        :reject-text="deleteRejectText"
        :confirm-value="inboxPendingDeletion?.name"
        :confirm-place-holder-text="confirmPlaceHolderText"
        @on-confirm="confirmDeletion"
        @on-close="closeDelete"
      />
      <InboxFinalDeleteModal
        v-if="showFinalDeletePopup"
        v-model:show="showFinalDeletePopup"
        :inbox-name="inboxPendingDeletion?.name"
        @on-confirm="finalConfirmDeletion"
        @on-close="closeFinalDelete"
      />
    </div>
  </div>
</template>
